import { useRouter } from 'next/router';

import React, { useEffect, useState, useMemo } from 'react';
import careerFilterOptions from '../../../lib/config/project/careerFilterOptions';
import jobsFilterOptions from '../../../lib/config/project/jobsFilterOptions';
import trainingFilterOptions from '../../../lib/config/project/trainingFilterOptions';

import useMyRouter from '../../../lib/hooks/myRouter';

import { SingleFilter } from '../../../lib/types';
import Times from '../../icons/Times';
import { useRegion } from '../../../lib/providers/regionProvider';

interface ActiveFiltersProps {
  page: 'training' | 'careers' | 'open-jobs' | 'resources';
  clearAllFilters?: () => void;
  searchOnly?: boolean;
}

const acceptableParams = [
  ...trainingFilterOptions.map((f) => f.paramLabel),
  ...careerFilterOptions.map((f) => f.paramLabel),
  ...jobsFilterOptions.map((f) => f.paramLabel),
  'institution',
  'search',
  'careers',
  'provider',
  'keyword',
];
const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  page,
  searchOnly,
  clearAllFilters,
}) => {
  const { updateRouter } = useMyRouter();
  const {
    region: { slug },
  } = useRegion();
  const router = useRouter();
  const { query } = router;
  const [activeFilters, setActiveFilters] = useState([] as SingleFilter[]);

  const updateActiveFilters = () => {
    const keys = searchOnly
      ? Object.keys(query).filter((k) => k == 'keyword')
      : Object.keys(query).filter((k) => acceptableParams.includes(k));

    const filters = [] as SingleFilter[];
    keys.forEach((key) => {
      const values = (query[key] as string).split('_');
      const singleFilters = values.map((filter) => {
        return { paramLabel: key, value: filter };
      });
      filters.push(...singleFilters);
    });
    setActiveFilters([...filters]);
  };

  // Update the active filters every time the query params change
  useEffect(updateActiveFilters, [query]);

  const pathname = useMemo(() => {
    return `/${page}`;
  }, [slug]);

  // To remove a single filter, just remove it from the
  // query params
  const removeSingleFilter = (filter: SingleFilter) => {
    const values = (query[filter.paramLabel] as string)?.split('_');
    const newValue = values?.filter((f) => f != filter.value).join('_');
    if (!newValue) {
      delete query[filter.paramLabel];
    } else {
      query[filter.paramLabel] = newValue;
    }
    delete query[':region'];
    updateRouter({
      pathname,
      query,
    });
  };

  if (!activeFilters?.length) return <></>;

  return (
    <div className="flex flex-row flex-wrap my-4">
      {activeFilters.map((filter: SingleFilter) => {
        return (
          <div
            key={filter.value}
            className="bg-[#E3E3E3] rounded-md py-1 px-2  flex items-center my-1 mr-2 h-9 opacity-100"
          >
            <p className="p-1.5 capitalize font-semibold text-[#383838] text-sm text-left font-base">
              {filter.value}
            </p>
            <Times
              className="w-5 h-5 outline-none cursor-pointer stroke-1 stroke-black"
              onClick={() => {
                removeSingleFilter(filter);
              }}
            />
          </div>
        );
      })}
      {!searchOnly && activeFilters.length > 0 && (
        <button
          className="text-[#013145] font-base font-bold ml-3.5 p-1.5 text-lg"
          onClick={clearAllFilters}
          tabIndex={1}
        >
          Clear All
        </button>
      )}
    </div>
  );
};

export default ActiveFilters;
